<template>
  <v-container
    fluid
    class="br-page"
    :class="{ 'br-page--dark': $vuetify.theme.dark }"
  >
    <section>
      <KpiCards class="mb-3" :items="kpiItems" :loading="!kpiReady" :skeleton="5" />

      <!-- Top performing branch spotlight -->
      <div
        v-if="topBranch && topBranch.sales > 0"
        class="br-spotlight-bar mb-3 d-flex align-center"
      >
        <div class="br-spotlight-badge mr-3">
          <v-icon size="15" color="primary">fal fa-trophy</v-icon>
        </div>
        <div class="br-spotlight-text d-flex align-center flex-wrap">
          <span class="br-spotlight-title">{{ $t("top_performing_branch") }}:</span>
          <strong class="ml-1 text-subtitle-2">{{ topBranch.name }}</strong>
          <span class="mx-2 text-muted">·</span>
          <span class="font-numeric font-weight-bold primary--text">
            {{ topBranch.sales | NumberFormat }} ₭
          </span>
          <span class="ml-2 text-caption text-muted">
            ({{ $t("top_branch_hint", { percent: topBranch.share }) }})
          </span>
        </div>
      </div>

      <!-- Main report panel -->
      <v-card class="br-panel mb-3" flat>
        <v-card-text class="pb-2 pt-3">
          <!-- Panel header: title + status tabs -->
          <div class="d-flex align-center flex-wrap br-panel-header">
            <span class="br-accent-bar" />
            <h1 class="br-panel-title">{{ $t("report_branches") }}</h1>
            <span class="br-panel-count">{{ pagination.total }} {{ $t("branches") }}</span>
            <v-spacer />
            <div class="br-tabs">
              <div
                v-for="t in statusList"
                :key="t.value"
                class="br-tab"
                :class="{ 'br-tab--active': status_filter === t.value }"
                @click="selectStatusTab(t.value)"
              >
                {{ t.text }}
                <span class="br-tab-count">{{ tabCount(t.value) }}</span>
              </div>
            </div>
          </div>

          <!-- Filter row -->
          <div class="d-flex align-center flex-wrap br-filter-row mt-3">
            <TextSearch
              v-model="search"
              class="br-search"
              @search="filterData()"
              @onClear="
                search = '';
                filterData();
              "
            />

            <!-- Date range picker -->
            <div class="d-flex align-center br-date-range">
              <DatePicker
                :dense="true"
                :placeholder="false"
                label="start_date"
                v-model="start_date"
                hide-details
                style="max-width: 152px"
                @input="filterData()"
              />
              <span class="br-date-arrow mx-1">→</span>
              <DatePicker
                :dense="true"
                :placeholder="false"
                label="end_date"
                v-model="end_date"
                hide-details
                style="max-width: 152px"
                @input="filterData()"
              />
            </div>

            <!-- Clear filters -->
            <span v-if="isFiltered" class="br-clear-link" @click="resetFilters">
              {{ $t("clear_filters") }}
            </span>

            <v-spacer />

            <!-- Export to Excel -->
            <div
              class="br-export-btn"
              :class="{ 'opacity-60': exporting }"
              @click="!exporting && handleExcelExport()"
            >
              <v-progress-circular
                v-if="exporting"
                size="13"
                width="2"
                indeterminate
                color="primary"
                class="mr-1"
              />
              <v-icon v-else size="13" class="mr-1">fal fa-file-export</v-icon>
              {{ $t("export") }}
            </div>
          </div>

          <!-- Data table -->
          <div class="br-table-wrap mt-3">
            <!-- Empty state -->
            <div
              v-if="!overlay && items.length === 0"
              class="d-flex flex-column align-center justify-center py-12"
            >
              <v-icon size="64" color="grey lighten-1">fal fa-store-alt-slash</v-icon>
              <div class="mt-4 text-subtitle-1 font-weight-medium grey--text text--darken-2">
                {{ $t("no_branches_found") }}
              </div>
              <div class="mt-1 grey--text text-body-2">
                {{ $t("no_branches_found_desc") }}
              </div>
            </div>

            <v-data-table
              v-else
              :headers="headers"
              :items="items"
              item-key="id"
              :loading="overlay"
              :loading-text="$t('loading')"
              :no-data-text="$t('no_data')"
              :server-items-length="pagination.total"
              :options.sync="options"
              :footer-props="{ 'items-per-page-options': [15, 30, 50] }"
              class="br-data-table elevation-0"
            >
              <!-- Index -->
              <template v-slot:item.index="{ index }">
                <span class="text-caption grey--text font-numeric">
                  {{ (pagination.page - 1) * pagination.per_page + index + 1 }}
                </span>
              </template>

              <!-- Branch Info (Logo, Name, Receipt size) -->
              <template v-slot:item.name="{ item }">
                <div class="d-flex align-center py-1">
                  <v-avatar size="36" class="mr-3 br-branch-avatar" rounded>
                    <app-img
                      v-if="item.logo"
                      :img="item.logo"
                      width="36"
                      height="36"
                      rounded
                    />
                    <v-icon v-else color="primary" size="18">fal fa-store</v-icon>
                  </v-avatar>
                  <div class="min-width-0">
                    <div
                      class="font-weight-bold text-body-2 text-truncate br-branch-name"
                      @click="openBranchDetail(item)"
                    >
                      {{ item.name }}
                    </div>
                    <div class="d-flex align-center mt-1">
                      <span class="br-tag br-tag--outline font-numeric">
                        {{ item.receipt_size || "80mm" }}
                      </span>
                    </div>
                  </div>
                </div>
              </template>

              <!-- Branch End Date -->
              <template v-slot:item.end_date="{ item }">
                <div class="d-flex align-center">
                  <span class="text-caption mr-2">{{ endDateLabel(item) }}</span>
                  <branch-status-chip :branch="item" />
                </div>
              </template>

              <!-- Status & Online badges -->
              <template v-slot:item.status="{ item }">
                <div class="d-flex align-center flex-wrap" style="gap: 4px">
                  <span
                    class="br-tag"
                    :class="item.is_active ? 'br-tag--success' : 'br-tag--muted'"
                  >
                    {{ item.is_active ? $t("active") : $t("inactive") }}
                  </span>
                  <span v-if="item.is_online" class="br-tag br-tag--info">
                    <v-icon x-small color="#1d4ed8" class="mr-1">fal fa-globe</v-icon>
                    {{ $t("online") }}
                  </span>
                </div>
              </template>

              <!-- Contact & Location -->
              <template v-slot:item.contact="{ item }">
                <div class="py-1">
                  <div v-if="item.phone_no || item.mobile_no" class="d-flex align-center text-caption font-numeric">
                    <v-icon x-small color="grey" class="mr-1">fal fa-phone</v-icon>
                    {{ item.phone_no || item.mobile_no }}
                  </div>
                  <div
                    v-if="item.address"
                    class="d-flex align-center text-caption grey--text text-truncate mt-1"
                    style="max-width: 170px"
                    :title="item.address"
                  >
                    <v-icon x-small color="grey" class="mr-1">fal fa-map-marker-alt</v-icon>
                    {{ item.address }}
                  </div>
                  <div v-if="!item.phone_no && !item.mobile_no && !item.address" class="text-caption grey--text">
                    -
                  </div>
                </div>
              </template>

              <!-- Staff Count -->
              <template v-slot:item.users_count="{ item }">
                <span class="text-body-2 font-numeric">
                  <v-icon x-small color="grey" class="mr-1">fal fa-user</v-icon>
                  {{ (item.users_count || 0) | NumberFormat }}
                </span>
              </template>

              <!-- Products Count -->
              <template v-slot:item.products_count="{ item }">
                <span class="text-body-2 font-numeric">
                  <v-icon x-small color="grey" class="mr-1">fal fa-box</v-icon>
                  {{ (item.products_count || 0) | NumberFormat }}
                </span>
              </template>

              <!-- Bills Count -->
              <template v-slot:item.bill_count="{ item }">
                <span class="text-body-2 font-numeric">
                  <v-icon x-small color="grey" class="mr-1">fal fa-receipt</v-icon>
                  {{ (item.bill_count || 0) | NumberFormat }}
                </span>
              </template>

              <!-- Total Sales & Revenue Share Bar -->
              <template v-slot:item.tickets_sum_total_price="{ item }">
                <div class="py-1">
                  <div class="font-weight-bold font-numeric br-sales-amount">
                    {{ (item.tickets_sum_total_price || 0) | NumberFormat }} ₭
                  </div>
                  <div class="d-flex align-center mt-1">
                    <div class="br-progress-bar">
                      <div
                        class="br-progress-fill"
                        :style="{ width: branchShare(item) + '%' }"
                      />
                    </div>
                    <span class="text-caption ml-2 grey--text font-numeric">
                      {{ branchShare(item) }}%
                    </span>
                  </div>
                </div>
              </template>

              <!-- Avg. Bill Value -->
              <template v-slot:item.avg_bill="{ item }">
                <span class="text-caption font-numeric grey--text text--darken-2">
                  {{ calcAvgBill(item) | NumberFormat }} ₭
                </span>
              </template>

              <!-- Actions -->
              <template v-slot:item.actions="{ item }">
                <v-tooltip bottom>
                  <template v-slot:activator="{ on, attrs }">
                    <v-btn
                      icon
                      small
                      color="primary"
                      v-bind="attrs"
                      v-on="on"
                      @click="openBranchDetail(item)"
                    >
                      <v-icon small>fal fa-eye</v-icon>
                    </v-btn>
                  </template>
                  <span>{{ $t("branch_details") }}</span>
                </v-tooltip>
              </template>
            </v-data-table>
          </div>
        </v-card-text>
      </v-card>

      <!-- Branch Detail Modal using AppCustomDialog -->
      <app-custom-dialog
        v-if="selectedBranch"
        v-model="dialogDetail"
        :title="selectedBranch.name"
        :is_translate="false"
        subtitle="branch_details"
        titleIcon="fal fa-store"
        titleIconColor="primary"
        width="540px"
        :footer="true"
        :showCancel="true"
        cancelText="close"
      >
        <div slot="activator" class="pt-2">
          <!-- Branch summary badges -->
          <div class="d-flex align-center justify-space-between mb-4 pb-3" style="border-bottom: 1px solid #f1f2f7">
            <div class="d-flex align-center">
              <v-avatar size="44" class="mr-3 br-branch-avatar" rounded>
                <app-img
                  v-if="selectedBranch.logo"
                  :img="selectedBranch.logo"
                  width="44"
                  height="44"
                  rounded
                />
                <v-icon v-else color="primary" size="22">fal fa-store</v-icon>
              </v-avatar>
              <div>
                <div class="font-weight-bold text-subtitle-2">{{ selectedBranch.name }}</div>
                <div class="d-flex align-center mt-1" style="gap: 6px">
                  <span
                    class="br-tag"
                    :class="selectedBranch.is_active ? 'br-tag--success' : 'br-tag--muted'"
                  >
                    {{ selectedBranch.is_active ? $t("active") : $t("inactive") }}
                  </span>
                  <span v-if="selectedBranch.is_online" class="br-tag br-tag--info">
                    <v-icon x-small color="#1d4ed8" class="mr-1">fal fa-globe</v-icon>
                    {{ $t("online") }}
                  </span>
                </div>
              </div>
            </div>
            <span class="br-tag br-tag--outline font-numeric">
              {{ selectedBranch.receipt_size || "80mm" }}
            </span>
          </div>

          <!-- Branch mini metrics -->
          <div class="br-dialog-kpis mb-4">
            <div class="br-dialog-kpi-item">
              <span class="text-caption grey--text">{{ $t("total_sales") }}</span>
              <strong class="font-numeric primary--text">
                {{ (selectedBranch.tickets_sum_total_price || 0) | NumberFormat }} ₭
              </strong>
            </div>
            <div class="br-dialog-kpi-item">
              <span class="text-caption grey--text">{{ $t("total_bills") }}</span>
              <strong class="font-numeric">
                {{ (selectedBranch.bill_count || 0) | NumberFormat }}
              </strong>
            </div>
            <div class="br-dialog-kpi-item">
              <span class="text-caption grey--text">{{ $t("total_products") }}</span>
              <strong class="font-numeric">
                {{ (selectedBranch.products_count || 0) | NumberFormat }}
              </strong>
            </div>
            <div class="br-dialog-kpi-item">
              <span class="text-caption grey--text">{{ $t("total_staff") }}</span>
              <strong class="font-numeric">
                {{ (selectedBranch.users_count || 0) | NumberFormat }}
              </strong>
            </div>
          </div>

          <!-- Profile Info list -->
          <v-list dense class="pa-0">
            <v-list-item class="px-0">
              <v-list-item-icon class="mr-3">
                <v-icon small color="grey">fal fa-phone</v-icon>
              </v-list-item-icon>
              <v-list-item-content>
                <v-list-item-subtitle class="text-caption grey--text">{{ $t("phon_no") }}</v-list-item-subtitle>
                <v-list-item-title class="font-numeric font-weight-medium">
                  {{ selectedBranch.phone_no || selectedBranch.mobile_no || "-" }}
                </v-list-item-title>
              </v-list-item-content>
            </v-list-item>

            <v-list-item class="px-0">
              <v-list-item-icon class="mr-3">
                <v-icon small color="grey">fal fa-map-marker-alt</v-icon>
              </v-list-item-icon>
              <v-list-item-content>
                <v-list-item-subtitle class="text-caption grey--text">{{ $t("address") }}</v-list-item-subtitle>
                <v-list-item-title class="font-weight-medium">
                  {{ selectedBranch.address || "-" }}
                </v-list-item-title>
              </v-list-item-content>
            </v-list-item>

            <v-list-item class="px-0">
              <v-list-item-icon class="mr-3">
                <v-icon small color="grey">fal fa-print</v-icon>
              </v-list-item-icon>
              <v-list-item-content>
                <v-list-item-subtitle class="text-caption grey--text">{{ $t("receipt_size") }}</v-list-item-subtitle>
                <v-list-item-title class="font-weight-medium font-numeric">
                  {{ selectedBranch.receipt_size || "80mm" }}
                </v-list-item-title>
              </v-list-item-content>
            </v-list-item>

            <v-list-item v-if="selectedBranch.created_at" class="px-0">
              <v-list-item-icon class="mr-3">
                <v-icon small color="grey">fal fa-calendar-alt</v-icon>
              </v-list-item-icon>
              <v-list-item-content>
                <v-list-item-subtitle class="text-caption grey--text">{{ $t("date") }}</v-list-item-subtitle>
                <v-list-item-title class="font-weight-medium font-numeric">
                  {{ selectedBranch.created_at | moment("DD/MM/YYYY HH:mm") }}
                </v-list-item-title>
              </v-list-item-content>
            </v-list-item>
          </v-list>
        </div>

        <div slot="actions">
          <v-btn
            depressed
            color="primary"
            class="custom-dialog-action"
            @click="$router.push('/manage-branches')"
          >
            <v-icon left small>fal fa-cog</v-icon>
            {{ $t("manage_branch") }}
          </v-btn>
        </div>
      </app-custom-dialog>

      <!-- AppLoading overlay -->
      <app-loading :overlay="overlay && !exporting" />
    </section>
  </v-container>
</template>

<script>
import { mapGetters, mapActions } from "vuex";
import { myMixin } from "@/mixin/main";
import AppCustomDialog from "@/components/customDialog";
import BranchStatusChip from "@/components/BranchStatusChip.vue";
import { branchEndDateLabel } from "@/utils/branchStatus";

export default {
  name: "BranchReport",
  mixins: [myMixin],
  components: {
    AppCustomDialog,
    BranchStatusChip,
  },

  data: () => ({
    overlay: false,
    kpiReady: false,
    exporting: false,
    dialogDetail: false,
    selectedBranch: null,
    search: "",
    status_filter: "all",
    start_date: "",
    end_date: "",
    items: [],
    pagination: {
      page: 1,
      per_page: 30,
      total: 0,
    },
    options: {
      page: 1,
      itemsPerPage: 30,
    },
    summary: {
      total_branches: 0,
      active_branches: 0,
      inactive_branches: 0,
      online_branches: 0,
      total_sales: 0,
      total_bills: 0,
      total_products: 0,
      total_users: 0,
      top_branch: null,
    },
  }),

  computed: {
    kpiItems() {
      const s = this.summary;
      return [
        {
          key: "branches",
          title: "total_branches",
          icon: "fal fa-store-alt",
          value: Number(s.total_branches || 0),
          note: `${s.active_branches} ${this.$t("active")} · ${s.inactive_branches} ${this.$t("inactive")}`,
        },
        {
          key: "sales",
          title: "total_sales",
          icon: "fal fa-coins",
          value: `${this.$options.filters.NumberFormat(s.total_sales)} ₭`,
          note: this.$t("all_branches_revenue"),
        },
        {
          key: "bills",
          title: "total_bills",
          icon: "fal fa-receipt",
          value: Number(s.total_bills || 0),
          note: this.$t("completed_orders"),
        },
        {
          key: "products",
          title: "total_products",
          icon: "fal fa-box-open",
          value: Number(s.total_products || 0),
          note: this.$t("catalog_items"),
        },
        {
          key: "staff",
          title: "total_staff",
          icon: "fal fa-user-friends",
          value: Number(s.total_users || 0),
          note: this.$t("active_personnel"),
        },
      ];
    },
    ...mapGetters({
      branches: "index/branches",
    }),

    statusList() {
      return [
        { text: this.$t("all"), value: "all" },
        { text: this.$t("active"), value: "active" },
        { text: this.$t("inactive"), value: "inactive" },
        { text: this.$t("online"), value: "online" },
      ];
    },

    isFiltered() {
      return (
        (this.search && this.search.trim() !== "") ||
        this.status_filter !== "all" ||
        Boolean(this.start_date) ||
        Boolean(this.end_date)
      );
    },

    headers() {
      return [
        { text: "#", value: "index", sortable: false, width: "48px", align: "center" },
        { text: this.$t("branch"), value: "name", sortable: true },
        { text: this.$t("branch_end_date"), value: "end_date", sortable: false },
        { text: this.$t("status"), value: "status", sortable: false, width: "120px" },
        { text: this.$t("address"), value: "contact", sortable: false },
        { text: this.$t("users_count"), value: "users_count", sortable: true, align: "center" },
        { text: this.$t("product"), value: "products_count", sortable: true, align: "center" },
        { text: this.$t("tickets_count"), value: "bill_count", sortable: true, align: "center" },
        { text: `${this.$t("price")} & ${this.$t("revenue_share")}`, value: "tickets_sum_total_price", sortable: true, minWidth: "170px" },
        { text: this.$t("avg_bill_value"), value: "avg_bill", sortable: false, align: "right" },
        { text: this.$t("manage"), value: "actions", sortable: false, width: "50px", align: "center" },
      ];
    },

    topBranch() {
      if (this.summary.top_branch && this.summary.top_branch.name) {
        return this.summary.top_branch;
      }
      if (!this.items.length) return null;
      const sorted = [...this.items].sort(
        (a, b) => (b.tickets_sum_total_price || 0) - (a.tickets_sum_total_price || 0)
      );
      const top = sorted[0];
      const total = this.summary.total_sales || 1;
      const sales = Number(top.tickets_sum_total_price || 0);
      return {
        name: top.name,
        sales,
        share: Math.round((sales / total) * 1000) / 10,
      };
    },
  },

  watch: {
    options: {
      handler(val) {
        if (
          val.page !== this.pagination.page ||
          val.itemsPerPage !== this.pagination.per_page
        ) {
          this.pagination.page = val.page;
          this.pagination.per_page = val.itemsPerPage;
          this.fetchItems();
        }
      },
      deep: true,
    },
  },

  async created() {
    await this.fetchData();
    this.kpiReady = true;
  },

  methods: {
    ...mapActions({
      fetch: "index/fetch",
    }),

    endDateLabel(item) {
      return branchEndDateLabel(item, this.$t.bind(this));
    },

    async fetchData() {
      this.overlay = true;
      try {
        await Promise.all([this.fetchItems(), this.fetchSummary()]);
      } finally {
        this.overlay = false;
      }
    },

    async fetchItems() {
      let url = `report_branch?page=${this.pagination.page}&per_page=${this.pagination.per_page}`;
      if (this.search && this.search.trim()) {
        url += `&q=${encodeURIComponent(this.search.trim())}`;
      }
      if (this.status_filter && this.status_filter !== "all") {
        url += `&status=${this.status_filter}`;
      }
      if (this.start_date && this.end_date) {
        url += `&start_date=${this.start_date}&end_date=${this.end_date}`;
      }

      try {
        const { data } = await this.$http.get(url);
        if (data.status && data.data) {
          const res = data.data;
          this.items = res.data || [];
          this.pagination.total = res.total || this.items.length;
          this.pagination.page = res.current_page || 1;
          this.pagination.per_page = res.per_page || 30;

          // Commit to Vuex for backward-compatibility
          this.$store.commit("index/SET_BRANCHES", res);
        }
      } catch (err) {
        console.error("fetchItems error:", err);
      }
    },

    async fetchSummary() {
      let url = "report_branch_summary?";
      if (this.start_date && this.end_date) {
        url += `start_date=${this.start_date}&end_date=${this.end_date}`;
      }
      try {
        const { data } = await this.$http.get(url, { skipErrorAlert: true });
        if (data.status && data.data && data.data.raw) {
          const raw = data.data.raw;
          this.summary = {
            total_branches: raw.total_branches || 0,
            active_branches: raw.active_branches || 0,
            inactive_branches: raw.inactive_branches || 0,
            online_branches: raw.online_branches || 0,
            total_sales: raw.total_sales || 0,
            total_bills: raw.total_bills || 0,
            total_products: raw.total_products || 0,
            total_users: raw.total_users || 0,
            top_branch: data.data.top_branch || null,
          };
        } else {
          this.computeFallbackSummary();
        }
      } catch (err) {
        this.computeFallbackSummary();
      }
    },

    computeFallbackSummary() {
      if (!this.items.length) return;
      const total = this.items.length;
      const active = this.items.filter((b) => b.is_active).length;
      const online = this.items.filter((b) => b.is_online).length;
      const sales = this.items.reduce(
        (sum, b) => sum + Number(b.tickets_sum_total_price || 0),
        0
      );
      const bills = this.items.reduce(
        (sum, b) => sum + Number(b.bill_count || 0),
        0
      );
      const products = this.items.reduce(
        (sum, b) => sum + Number(b.products_count || 0),
        0
      );
      const users = this.items.reduce(
        (sum, b) => sum + Number(b.users_count || 0),
        0
      );

      this.summary = {
        total_branches: total,
        active_branches: active,
        inactive_branches: total - active,
        online_branches: online,
        total_sales: sales,
        total_bills: bills,
        total_products: products,
        total_users: users,
        top_branch: null,
      };
    },

    filterData() {
      this.pagination.page = 1;
      this.options.page = 1;
      this.fetchData();
    },

    selectStatusTab(tab) {
      this.status_filter = tab;
      this.filterData();
    },

    resetFilters() {
      this.search = "";
      this.status_filter = "all";
      this.start_date = "";
      this.end_date = "";
      this.filterData();
    },

    tabCount(status) {
      if (status === "all") return this.summary.total_branches;
      if (status === "active") return this.summary.active_branches;
      if (status === "inactive") return this.summary.inactive_branches;
      if (status === "online") return this.summary.online_branches;
      return 0;
    },

    branchShare(item) {
      const total = Number(this.summary.total_sales || 0);
      if (!total) return 0;
      const sales = Number(item.tickets_sum_total_price || 0);
      return Math.min(100, Math.round((sales / total) * 1000) / 10);
    },

    calcAvgBill(item) {
      const sales = Number(item.tickets_sum_total_price || 0);
      const bills = Number(item.bill_count || 0);
      return bills > 0 ? Math.round(sales / bills) : 0;
    },

    openBranchDetail(branch) {
      this.selectedBranch = branch;
      this.dialogDetail = true;
    },

    async handleExcelExport() {
      if (this.exporting) return;
      this.exporting = true;
      let blobUrl = null;
      try {
        let url = `branch_report_export?locale=${this.$i18n.locale}`;
        if (this.search && this.search.trim()) {
          url += `&q=${encodeURIComponent(this.search.trim())}`;
        }
        if (this.status_filter && this.status_filter !== "all") {
          url += `&status=${this.status_filter}`;
        }
        if (this.start_date && this.end_date) {
          url += `&start_date=${this.start_date}&end_date=${this.end_date}`;
        }

        const { data } = await this.$http.get(url, {
          responseType: "blob",
          skipErrorAlert: true,
        });

        if (!data || !data.size || /json|text\/html/.test(data.type)) {
          throw new Error("Invalid export response");
        }

        blobUrl = window.URL.createObjectURL(data);
        const link = document.createElement("a");
        link.href = blobUrl;
        const dateStr = this.start_date && this.end_date
          ? `${this.start_date}_to_${this.end_date}`
          : "all_time";
        link.setAttribute("download", `branch_report_${dateStr}.xlsx`);
        document.body.appendChild(link);
        link.click();
        link.remove();
        this.$toast.success(this.$t("export_success"));
      } catch (err) {
        console.error("Excel export error:", err);
        this.$toast.error(this.$t("export_failed"));
      } finally {
        if (blobUrl) window.URL.revokeObjectURL(blobUrl);
        this.exporting = false;
      }
    },
  },
};
</script>

<style scoped>
.br-page {
  --br-accent: var(--v-primary-base, #5b39c9);
  --br-accent-soft: rgba(91, 57, 201, 0.12);
  --br-accent-softer: rgba(91, 57, 201, 0.07);
  --br-accent-border: rgba(91, 57, 201, 0.35);
  --br-border: #e2e8f0;
  --br-muted: #94a3b8;
  --br-text: #141021;
  --br-radius-lg: 16px;
  --br-radius-md: 10px;
  max-width: 100%;
  background: #f6f6fa;
}

.br-page--dark {
  --br-text: #ffffff;
  --br-border: rgba(255, 255, 255, 0.08);
  background: #1b1d30;
}

.br-page--dark .br-panel,
.br-page--dark .br-panel.v-card.v-sheet {
  background: #1e1e2d !important;
  border-color: rgba(255, 255, 255, 0.08) !important;
}

.br-page section {
  width: 100%;
}

/* KPI cards grid */

/* Spotlight bar */
.br-spotlight-bar {
  background: rgba(91, 57, 201, 0.05);
  border: 1px solid rgba(91, 57, 201, 0.2);
  border-radius: 12px;
  padding: 10px 16px;
}
.br-spotlight-badge {
  width: 28px;
  height: 28px;
  border-radius: 8px;
  background: rgba(91, 57, 201, 0.12);
  display: flex;
  align-items: center;
  justify-content: center;
}
.br-spotlight-title {
  font-size: 12px;
  font-weight: 600;
  color: var(--br-muted);
  text-transform: uppercase;
  letter-spacing: 0.04em;
}

/* Panel */
.br-panel,
.br-panel.v-card.v-sheet {
  background: #ffffff !important;
  border: 1px solid var(--br-border) !important;
  border-radius: 14px !important;
  box-shadow: none !important;
}
.br-panel-header {
  gap: 10px;
}
.br-accent-bar {
  width: 3px;
  height: 15px;
  background: var(--br-accent);
  border-radius: 2px;
}
.br-panel-title {
  margin: 0;
  font-size: 17px;
  font-weight: 500;
  color: var(--br-text);
}
.br-panel-count {
  font-size: 11.5px;
  color: var(--br-muted);
}
.br-tabs {
  display: flex;
  height: 38px;
  box-sizing: border-box;
  align-items: center;
  gap: 4px;
  padding: 3px;
  border-radius: 10px;
  background: #f1f2f7;
  border: 1px solid var(--br-border);
}
.br-tab {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  height: 30px;
  padding: 4px 12px;
  border-radius: 7px;
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;
  user-select: none;
  color: var(--br-muted);
  transition: all 0.15s ease;
}
.br-tab--active {
  color: #5b39c9 !important;
  background: #ffffff !important;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.06);
  font-weight: 700;
}
.br-page--dark .br-tab--active {
  color: #c4b5fd !important;
  background: rgba(154, 140, 240, 0.18) !important;
  box-shadow: none;
}
.br-tab-count {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 17px;
  height: 17px;
  margin-left: 4px;
  padding: 0 4px;
  border-radius: 999px;
  font-size: 10px;
  font-variant-numeric: tabular-nums;
  background: rgba(148, 163, 184, 0.14);
}
.br-tab--active .br-tab-count {
  background: rgba(91, 57, 201, 0.14);
  color: #5b39c9;
}
.br-page--dark .br-tab--active .br-tab-count {
  background: rgba(167, 139, 250, 0.25);
  color: #c4b5fd;
}

/* Filter row */
.br-filter-row {
  gap: 10px;
}
.br-search {
  flex: 1 1 220px;
  min-width: 200px;
  max-width: 300px;
}
.br-date-range {
  gap: 4px;
}
.br-date-arrow {
  color: var(--br-muted);
}
.br-clear-link {
  font-size: 12px;
  color: var(--br-accent);
  cursor: pointer;
  margin-left: 4px;
}
.br-clear-link:hover {
  text-decoration: underline;
}
.br-export-btn {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 8px 13px;
  border-radius: var(--br-radius-md);
  border: 1px solid var(--br-border);
  color: var(--br-muted);
  font-size: 13px;
  cursor: pointer;
  transition: color 0.15s ease, border-color 0.15s ease;
  user-select: none;
}
.br-export-btn:hover {
  color: var(--br-text);
  border-color: #cbd5e1;
}

/* Branch cell */
.br-branch-avatar {
  background: rgba(91, 57, 201, 0.08);
  border: 1px solid rgba(91, 57, 201, 0.2);
}
.br-branch-name {
  color: var(--br-text);
  cursor: pointer;
}
.br-branch-name:hover {
  color: var(--br-accent);
  text-decoration: underline;
}
.br-sales-amount {
  color: var(--br-accent);
  font-size: 13.5px;
}

/* Progress bar */
.br-progress-bar {
  width: 90px;
  height: 6px;
  background: #f1f2f7;
  border-radius: 999px;
  overflow: hidden;
}
.br-progress-fill {
  height: 100%;
  background: var(--br-accent);
  border-radius: 999px;
  transition: width 0.3s ease;
}

/* Tags */
.br-tag {
  display: inline-block;
  font-size: 11px;
  padding: 2px 8px;
  border-radius: 999px;
  white-space: nowrap;
  border: 1px solid transparent;
}
.br-tag--success {
  color: #15803d;
  background: #f0fdf4;
  border-color: #bbf7d0;
}
.br-tag--muted {
  color: #64748b;
  background: #f8fafc;
  border-color: #e2e8f0;
}
.br-tag--info {
  color: #1d4ed8;
  background: #eff6ff;
  border-color: #bfdbfe;
}
.br-tag--outline {
  color: var(--br-muted);
  background: #f8fafc;
  border-color: #e2e8f0;
  font-size: 10px;
  padding: 1px 6px;
}

/* Table styling */
.br-table-wrap ::v-deep .v-data-table {
  background: transparent !important;
}
.br-table-wrap ::v-deep .v-data-table-header th {
  font-size: 11px !important;
  font-weight: 700 !important;
  letter-spacing: 0.06em !important;
  text-transform: uppercase !important;
  color: var(--br-muted) !important;
  border-bottom: 1px solid var(--br-border) !important;
  background: transparent !important;
  background-color: transparent !important;
  box-shadow: none !important;
}
.br-table-wrap ::v-deep .v-data-table tbody tr {
  border-bottom: 1px solid #f1f2f7 !important;
}
.br-table-wrap ::v-deep .v-data-table tbody tr:hover {
  background: var(--br-accent-softer) !important;
}

.br-page--dark .br-table-wrap ::v-deep .v-data-table-header th {
  background: transparent !important;
  background-color: transparent !important;
  box-shadow: none !important;
  border-bottom-color: rgba(255, 255, 255, 0.08) !important;
}
.br-page--dark .br-table-wrap ::v-deep .v-data-table tbody tr {
  border-bottom-color: rgba(255, 255, 255, 0.06) !important;
}
.br-page--dark .br-progress-bar {
  background: #262638;
}
.br-page--dark .br-tag--muted,
.br-page--dark .br-tag--outline {
  background: #262638;
  border-color: rgba(255, 255, 255, 0.12);
  color: #cbd5e1;
}
.br-page--dark .br-dialog-kpi-item,
.theme--dark .br-dialog-kpi-item {
  background: #262638;
  border-color: rgba(255, 255, 255, 0.08);
  color: #f1f5f9;
}

/* Dialog styles */
.br-dialog-kpis {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 8px;
}
.br-dialog-kpi-item {
  padding: 10px 12px;
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  border-radius: 10px;
  display: flex;
  flex-direction: column;
}
.br-dialog-kpi-item strong {
  font-size: 15px;
  margin-top: 2px;
}

.font-numeric {
  font-variant-numeric: tabular-nums;
}

@media (max-width: 960px) {
  .br-page {
    padding: 12px !important;
  }
  .br-tabs {
    width: 100%;
    overflow-x: auto;
  }
  .br-filter-row {
    align-items: stretch !important;
  }
  .br-search {
    flex: 1 1 100%;
    max-width: none;
  }
  .br-date-range {
    flex: 1 1 100%;
  }
}
</style>
